/**
 * Dashboard Page - Role-based landing page
 */
import React, { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/providers/AuthProvider';
import { Skeleton } from '../components/ui/skeleton';

// Role-specific dashboards
import RiskManagerDashboard from '../components/dashboard/RiskManagerDashboard';
import CollectionsManagerDashboard from '../components/dashboard/CollectionsManagerDashboard';

// New dashboard components
import { NewDashboardKPICards } from '../components/dashboard/NewDashboardKPICards';
import { DashboardListSection } from '../components/dashboard/DashboardListSection';
import { NewDPDDistribution } from '../components/dashboard/NewDPDDistribution';
import { DisbursementTrendChart } from '../components/dashboard/DisbursementTrendChart';
import {
  FileText, CheckCircle, Lock, AlertTriangle,
  AlertTriangle as AlertTriangleIcon, DollarSign, FileX2, Shield, Clock, CalendarClock,
} from 'lucide-react';
import { formatCurrency } from '@/lib/formatters';

function getRoleDashboard(user) {
  if (!user) return 'admin';
  if (user.is_tenant_admin) return 'admin';
  const roles = user.roles || [];
  // Priority: risk_manager > collection_manager > default admin
  if (roles.includes('risk_manager')) return 'risk_manager';
  if (roles.includes('collection_manager')) return 'collection_manager';
  return 'admin';
}

function TenantAdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/dashboard/stats')
      .then((statsData) => {
        setStats(statsData);
      })
      .catch(e => toast.error(e.message || 'Failed to load dashboard data'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="admin-dashboard">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {['s1','s2','s3','s4'].map(id => <Skeleton key={id} className="h-32 rounded-xl" />)}
        </div>
      </div>
    );
  }

  // Helper function to format date
  const formatAccrualDate = (dateString) => {
    if (!dateString) return 'Never';
    const d = new Date(dateString);
    const time = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
    const date = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
    return `${time}, ${date}`;
  };

  // Loan Portfolio items
  const loanPortfolioItems = [
    {
      icon: FileText,
      label: 'Total Loans',
      value: stats?.total_loans || 0,
      color: 'text-blue-600 dark:text-blue-400',
    },
    {
      icon: CheckCircle,
      label: 'Active Loans',
      value: stats?.active_loans || 0,
      color: 'text-green-600 dark:text-green-400',
    },
    {
      icon: Lock,
      label: 'Closed Loans',
      value: stats?.closed_loans || 0,
      color: 'text-gray-600 dark:text-gray-400',
    },
    {
      icon: AlertTriangle,
      label: 'NPA Loans',
      value: stats?.npa_loans || 0,
      color: 'text-red-600 dark:text-red-400',
    },
  ];

  // Key Financials items
  const keyFinancialsItems = [
    {
      icon: AlertTriangleIcon,
      label: 'NPA Amount',
      value: formatCurrency(stats?.npa_amount || 0),
      color: 'text-red-600 dark:text-red-400',
    },
    {
      icon: DollarSign,
      label: 'Shortfall Amount',
      value: formatCurrency(stats?.total_shortfall_amount || 0),
      color: 'text-yellow-600 dark:text-yellow-400',
    },
    {
      icon: FileX2,
      label: 'Write Off',
      value: formatCurrency(stats?.total_write_off || 0),
      color: 'text-purple-600 dark:text-purple-400',
    },
    {
      icon: Shield,
      label: 'Securities',
      value: stats?.active_securities || 0,
      color: 'text-green-600 dark:text-green-400',
    },
  ];

  // Operational Metrics items
  const operationalMetricsItems = [
    {
      icon: Clock,
      label: 'Unpaid Shortfall Applicants',
      value: stats?.unpaid_shortfall_applicants || 0,
      color: 'text-orange-600 dark:text-orange-400',
    },
    {
      icon: CheckCircle,
      label: 'Active Securities',
      value: stats?.active_securities || 0,
      color: 'text-green-600 dark:text-green-400',
    },
    {
      icon: CalendarClock,
      label: 'Last Interest Accrual',
      value: formatAccrualDate(stats?.last_interest_accrual_date),
      color: 'text-purple-600 dark:text-purple-400',
    },
  ];

  return (
    <div className="space-y-6 w-full max-w-[1600px]" data-testid="admin-dashboard">
      {/* Top KPI Cards with Trends */}
      <NewDashboardKPICards stats={stats} />

      {/* Loan Portfolio + Key Financials & Risk side by side.
          Key Financials & Risk moved up into this row (previously it sat two rows
          lower, leaving the column to the right of Loan Portfolio blank). Fills
          the empty space and removes the vertical gap; still stacks on mobile. */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <DashboardListSection
          title="Loan Portfolio"
          linkTo="/loans"
          items={loanPortfolioItems}
        />

        <DashboardListSection
          title="Key Financials & Risk"
          items={keyFinancialsItems}
        />
      </div>

      {/* DPD Distribution & Disbursement Trend */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <NewDPDDistribution stats={stats} />
        <DisbursementTrendChart />
      </div>

      {/* Operational Metrics */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <DashboardListSection
          title="Operational Metrics"
          items={operationalMetricsItems}
        />
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const dashboardType = getRoleDashboard(user);

  switch (dashboardType) {
    case 'risk_manager':
      return <RiskManagerDashboard />;
    case 'collection_manager':
      return <CollectionsManagerDashboard />;
    default:
      return <TenantAdminDashboard />;
  }
}
